<script setup lang="ts">
/**
 * App.vue — root three-column flex-shell per 03-ui-design.md section 1.
 *
 *  ┌──────────┬───────────────────────────────────┬──────────────┐
 *  │          │  Sticky filter-header             │              │
 *  │ Sidebar  ├───────────────────────────────────┤   Filter     │
 *  │ (sticky) │  <RouterView/> (scrolls)          │   drawer     │
 *  │          │                                   │   (push)     │
 *  └──────────┴───────────────────────────────────┴──────────────┘
 *
 * Per D-026: background uses bg-page (warm off-white in light, near-black
 * in dark) and primary text uses text-ink. Ingen footer — branding ligger
 * i venstre sidebar (§1 i 03-ui-design.md).
 */

import { computed } from 'vue';
import { useRoute } from 'vue-router';

import AppSidebar from './components/AppSidebar.vue';
import DocPatchLayer from './components/docs/DocPatchLayer.vue';
import FilterDrawer from './components/FilterDrawer.vue';
import FilterShell from './components/FilterShell.vue';
import ActionDrawer from './components/ActionDrawer.vue';
import PageSettingsDrawer from './components/PageSettingsDrawer.vue';
import SavingIndicator from './components/SavingIndicator.vue';
import UserAccessToasts from './components/UserAccessToasts.vue';
import { isBrandUi } from './composables/useBrandUi';

const route = useRoute();
// Public-ruter (fx login) renderes uden app-shell (ingen sidebar/filter).
// [D-420]: monitor-visningen (kampagne-dashboards) tegnes også uden app-skal.
// [D-459]: meta.bare (kampagnebriefen) tegnes uden app-skal, men auth-gaten i
// routeren gælder uændret.
const isPublic = computed(() => route.meta.public === true || route.meta.monitor === true || route.meta.bare === true);

/** Nøglen for ruteskiftets overgang ([D-471]): rutens MØNSTER, ikke den fulde
 *  adresse, så filtre i URL'en (§23) aldrig tegner siden forfra. */
const pageKey = computed(() => route.matched[route.matched.length - 1]?.path ?? route.path);
</script>

<template>
  <!--
    Natural body-scroll-layout: kun sidebar er sticky (h-screen + sticky
    top-0), middle column flyder naturligt med indholdet. Det betyder:
      • FilterShell scroller MED indholdet (ikke sticky)
      • Ingen overflow-y-auto på <main> → ingen indre scrollbar, ingen
        clipping af absolute-positionerede children (fx HelpIcon-tooltips
        der ellers blev klippet ved main's venstre kant pga. overflow-x:
        auto fra mixing-axes-reglen).
    Sidebar forbliver synlig via sticky top-0 h-screen i AppSidebar.vue.
  -->
  <RouterView v-if="isPublic" />

  <!-- print:block/print:min-h-0: Chromium klipper/blanker print-preview når
       indholdet står i flex-/min-h-screen-forfædre — ved print flades skallen
       ud til alm. block-flow (sidebar/header/drawer er print:hidden). -->
  <div v-else class="min-h-screen flex bg-page text-ink print:block print:min-h-0">
    <AppSidebar />

    <div class="flex-1 flex flex-col min-w-0 print:block">
      <FilterShell />

      <!-- Bund-padding = mobilens footer-menu (--mobile-nav-h, 0 på desktop),
           så sidens sidste række ikke ligger under baren. -->
      <main class="flex-1 pb-[var(--mobile-nav-h)] print:pb-0">
        <!-- Rettelseslaget ([D-478]): Dokumentationsvagtens tekstrettelser på
             dokumentationssiderne. Tegner intet på andre sider. -->
        <DocPatchLayer />
        <!-- Brand-udtrykket ([D-471]): siden toner blødt ud og ind ved ruteskift.
             Wrapperen er nødvendig, fordi de fleste sider har flere rødder
             (Teleport + indhold), og <Transition> kun kan animere ét element.
             Uden udtrykket er RouterView præcis som før. -->
        <RouterView v-if="!isBrandUi" />
        <RouterView v-else v-slot="{ Component }">
          <Transition name="bf-page" mode="out-in">
            <div :key="pageKey"><component :is="Component" /></div>
          </Transition>
        </RouterView>
      </main>
    </div>

    <FilterDrawer />
    <!-- Sideindstillinger (12-08-2026): tandhjuls-draweren — gensidigt
         udelukkende med filter-draweren (useDrawer/useSettingsDrawer). -->
    <PageSettingsDrawer />
    <!-- Handlings-draweren ([D-334]): sidens handlingsknap (fx "Tilpas
         varenumre" på Lagervarer) åbner den; gensidigt udelukkende med de 2
         ovenfor (useActionDrawer). -->
    <ActionDrawer />
  </div>

  <!-- Global gem-indikator (slide-up i footeren) — session 49. Teleporter til
       body, så den vises på tværs af både public- og app-ruter. -->
  <SavingIndicator />

  <!-- Tilgangs-notifikationer ([D-218]): hvem er netop gået ind i appen.
       Komponenten tegner INTET for andre end super_admins og poller kun for
       dem. Står uden for app-shellen, så den også dækker public-ruter. -->
  <UserAccessToasts />
</template>
